"use client";
import { useEffect, useRef } from "react";
import { useSocketContext } from "@/contexts/socketContext";
import { eventBus } from "@/services-frontend/comment/eventBus";
import { useWorkspaceContext } from "../workspaceContext";
import { useRootPagesOrder } from "../rootPagesOrderContext";
import { useAuth } from "@/hooks/use-auth";
import { useBlockActions } from "@/contexts/blockContext";
import { appDB, removePageFromAllMutation, addPrivatePageMutation } from "@/indexedDB";

// ─────────────────────────────────────────────────────────────────────────────
// Socket Event Router
//
// Single source of truth for translating server "client-ping" events into
// per-domain invalidation events on the eventBus. Each domain context listens
// for its own invalidate event and decides when/how to refetch.
//
// Rules:
//  - originSocketId === this tab's socket → skip self-echo. Keyed on the SOCKET,
//    not the user: an email match would also suppress a second tab of the same
//    person, which is a legitimate receiver. Writes are local-first, so acting
//    on our own echo refetches server state that has not been synced yet — the
//    item we just created vanishes and reappears.
//  - Optimistic sidebar updates (PUBLIC_PAGE / WORKAREA_PAGE) keep their
//    direct calls because the ping payload already carries the data needed.
//  - All other domains fan out via eventBus only; the listener in each
//    context decides whether to refetch.
// ─────────────────────────────────────────────────────────────────────────────
export const NotificationSocketListener = () => {
  const { socket } = useSocketContext();
  const { currentWorkspace } = useWorkspaceContext();
  const { addPublicPage, addWorkAreaPage, addPrivatePage } = useRootPagesOrder();
  const { user } = useAuth();

  const { updateBlock } = useBlockActions();
  const updateBlockRef = useRef(updateBlock);
  updateBlockRef.current = updateBlock;

  const workspaceIdRef = useRef<string>("");
  workspaceIdRef.current = currentWorkspace?._id ?? "";

  const currentUserEmailRef = useRef<string>("");
  currentUserEmailRef.current = user?.email ?? "";

  useEffect(() => {
    if (!socket) return;

    const handleClientPing = (data: any) => {
      const { actionType, noteId, commentId, pageId, workareaId, actorId, originSocketId } =
        data || {};

      // Self-echo skip, by ORIGIN TAB. This exact tab already applied the
      // change optimistically; re-acting on it refetches not-yet-synced server
      // state and undoes the optimistic update until sync catches up.
      // Server-initiated events (calendar webhooks, agents) carry no
      // originSocketId, so they are never skipped.
      if (originSocketId && socket?.id && originSocketId === socket.id) return;

      console.log(
        `\n[SocketEventRouter] 📢 client-ping`,
        `\n  actionType : ${actionType || "N/A"}`,
        `\n  noteId     : ${noteId || "N/A"}`,
        `\n  pageId     : ${pageId || "N/A"}`,
        `\n  actorId    : ${actorId || "N/A"}`,
      );

      switch (actionType) {
        case "NEW_COMMENT":
        case "MENTION_COMMENT":
          // Comment thread changed — CommentContext refetches if mounted.
          // Pass commentId so the receiver can fetch the new thread even when
          // its local block.comments[] hasn't been updated yet.
          eventBus.emit("comment:invalidate", { noteId, commentId, actionType });
          // MENTION_COMMENT also bumps the notification list.
          if (actionType === "MENTION_COMMENT") {
            eventBus.emit("notifications:invalidate", { reason: actionType });
          }
          break;

        case "COMMENT_REACTION":
          // Per-user reaction delta on one message — CommentContext applies
          // it in place (idempotent), no thread refetch.
          eventBus.emit("comment:reaction", data);
          break;

        case "COMMENT_RESOLVED":
          // Global resolved-flag delta for a batch of message ids —
          // CommentContext applies it in place, no thread refetch.
          eventBus.emit("comment:resolved", data);
          break;

        case "JOIN_REQUEST":
        case "JOIN_REQUEST_DECISION":
        case "ASSIGNED":
        case "REMINDER":
          // NotificationContext listens and refetches the list.
          eventBus.emit("notifications:invalidate", { reason: actionType });
          break;

        case "SHARE":
          // A page was shared with this user. Refresh notifications for the
          // badge, and ping the page list so the new page appears in their
          // sidebar (under shared / restricted, depending on access level).
          eventBus.emit("notifications:invalidate", { reason: actionType });
          eventBus.emit("pages:invalidate", { scope: "shared", noteId });
          break;

        case "PUBLIC_PAGE":
          if (pageId) addPublicPage(pageId);
          eventBus.emit("pages:invalidate", { scope: "public", pageId });
          break;

        case "PRIVATE_PAGE": {
          // A private page was created for THIS user server-side (e.g. an
          // auto-generated meeting page from Google Calendar). Mirror a local
          // create so it appears in the sidebar live: the sidebar resolves
          // blocks from IndexedDB only (never the API), so we must hydrate the
          // block record ourselves, then add the id to the private order.
          const block = data?.block;
          const wsId = data?.workspaceId || workspaceIdRef.current;
          console.log(
            `[SocketEventRouter] 🆕 PRIVATE_PAGE pageId=${pageId} wsId=${wsId} hasBlock=${!!block?._id}`,
          );
          if (pageId && wsId) {
            (async () => {
              try {
                if (block && block._id) {
                  await appDB.blocks.put(block);
                }
                // skipSync=true: this is a server action, not a local edit —
                // we must not enqueue our own sync op for it.
                await addPrivatePageMutation(String(pageId), wsId, undefined, true);
                addPrivatePage(String(pageId));
                console.log(`[SocketEventRouter] ✓ private page ${pageId} added to sidebar`);
              } catch (err) {
                console.error("[SocketEventRouter] private page add failed:", err);
              }
            })();
          }
          eventBus.emit("pages:invalidate", { scope: "private", pageId });
          break;
        }

        case "WORKAREA_PAGE":
          if (pageId && workareaId) addWorkAreaPage(workareaId, pageId);
          eventBus.emit("pages:invalidate", { scope: "workarea", workareaId, pageId });
          break;

        case "RESTRICTED_PAGE":
          eventBus.emit("pages:invalidate", { scope: "restricted", pageId, workareaId });
          break;

        case "DATASOURCE_PAGE_ADDED":
          eventBus.emit("datasource:invalidate", {
            dataSourceId: data?.dataSourceId,
            view_databaseId: data?.view_databaseId,
            pageId: data?.pageId,
          });
          break;

        case "DATASOURCE_UPDATED":
          // Fired by the database-source controller's interceptor on any
          // view/property/setting change (filter, sort, group, layout,
          // create/delete property, view type changes, etc.). Receiver
          // resolves the affected dataSource via dataSourceId or blockId.
          eventBus.emit("datasource:invalidate", {
            dataSourceId: data?.dataSourceId,
            blockId: data?.blockId,
            viewTypeId: data?.viewTypeId,
          });
          break;

        // ── Page lifecycle (v2) ────────────────────────────────────────────
        case "PAGE_UPDATED":
          // Title/icon of a page changed. Refresh the sidebar/root data so
          // renames appear live for members who aren't on the page (those on
          // it get it through the Yjs meta channel).
          eventBus.emit("pages:invalidate", { scope: "updated", pageId });
          eventBus.emit("page:meta-updated", { pageId, workareaId });
          break;

        case "BLOCK_UPDATED":
          // ROW-canonical blocks (button, …). Their state lives on the block
          // row and renders via useLiveBlock — the CRDT never carries the
          // change, so this ping names the blocks and boardContext refetches
          // + upserts them. Generic on purpose: a future row-canonical block
          // type only needs its blockType added to the API's
          // ROW_CANONICAL_BLOCK_TYPES list (note.controller batch-update);
          // this path needs no change.
          eventBus.emit("blocks:invalidate", {
            noteId,
            blockIds: (data as any)?.blockIds ?? [],
          });
          break;

        case "PAGE_TRASHED":
        case "PAGE_DELETED": {
          // Remove the page from THIS client's local stores explicitly.
          //
          // Refetching root data is not enough: the sidebar renders from a
          // Dexie live query over IndexedDB, and the refresh path only
          // *upserts* what the API returns — it never deletes rows that
          // vanished. purgeOrphanRootPages() covers part of this, but only
          // for root pages (parentType workspace/workarea), so a nested
          // subpage would linger in the sidebar indefinitely.
          //
          // Mirror what the acting client does locally: mark the block
          // deleted (useLiveBlocks filters status !== "deleted") and drop it
          // from every sidebar list. skipSync=true because this is someone
          // else's action — we must not enqueue our own sync op for it.
          if (pageId) {
            const targetId = String(pageId);
            const wsId = workspaceIdRef.current;
            if (wsId) {
              removePageFromAllMutation(targetId, wsId, true).catch((err) =>
                console.error("[SocketEventRouter] sidebar removal failed:", err),
              );
            }
            // The sidebar resolves blocks store-first (useResolvedBlocks), so
            // the IDB write below isn't enough — flip the context copy too or
            // the page keeps rendering as alive.
            updateBlockRef.current(targetId, { status: "deleted" }, "remote");
            (async () => {
              try {
                const existing = await appDB.blocks.get(targetId);
                if (existing && existing.status !== "deleted") {
                  // Prefer the authoritative deletedAt/deletedBy from the event
                  // payload (the backend has both at emit time). Older backends
                  // don't send them yet — fall back to receive-time and the
                  // actor's email so the TrashBanner can still resolve a name.
                  const deletedBy =
                    data?.deletedBy ??
                    (actorId
                      ? { userId: "", userName: "", userEmail: String(actorId) }
                      : undefined);
                  await appDB.blocks.put({
                    ...existing,
                    status: "deleted",
                    deletedAt: data?.deletedAt ?? new Date().toISOString(),
                    ...(deletedBy ? { deletedBy } : {}),
                  });
                }
              } catch (err) {
                console.error("[SocketEventRouter] local page removal failed:", err);
              }
            })();
          }

          eventBus.emit("pages:invalidate", { scope: "removed", pageId });
          eventBus.emit("page:removed", { pageId, actionType });
          break;
        }

        case "PAGE_RESTORED":
          eventBus.emit("pages:invalidate", { scope: "restored", pageId });
          break;

        case "PAGE_MOVED":
          // A page was reordered / re-parented in the sidebar. Refresh root
          // data so the new position/hierarchy shows for everyone.
          eventBus.emit("pages:invalidate", { scope: "moved" });
          break;

        // ── Workspace membership (v2) ──────────────────────────────────────
        case "MEMBER_ADDED":
        case "MEMBER_REMOVED":
        case "MEMBER_ROLE_UPDATED":
          // Someone's membership/role changed — refresh the member list so
          // avatars, people settings and @-mention pickers stay current.
          eventBus.emit("workspace:members-invalidate", {
            workspaceId: data?.workspaceId,
            actionType,
          });
          break;

        case "WORKSPACE_ACCESS_GRANTED":
          // This user was just added to a workspace — pull the workspace list
          // so it appears in their switcher without a reload.
          eventBus.emit("workspace:list-invalidate", { reason: actionType });
          eventBus.emit("notifications:invalidate", { reason: actionType });
          break;

        case "WORKSPACE_ACCESS_REVOKED":
        case "ROLE_CHANGED":
          // This user's own access changed. Refresh their workspace list (a
          // revoked workspace disappears) and re-run root data so restricted
          // pages they can no longer see drop out. ROLE_CHANGED also lets the
          // client re-join rooms at the new privilege level on next connect.
          eventBus.emit("workspace:list-invalidate", { reason: actionType });
          eventBus.emit("pages:invalidate", { scope: "access-changed" });
          break;

        default:
          console.warn(`[SocketEventRouter] ⚠ Unknown actionType: "${actionType}"`);
      }
    };

    socket.off("client-ping", handleClientPing);
    socket.on("client-ping", handleClientPing);

    return () => {
      socket.off("client-ping", handleClientPing);
    };
  }, [socket, addPublicPage, addWorkAreaPage, addPrivatePage]);

  return null;
};
